'use client'

import { dataTestIds } from 'plp/constants/dataTestIds'
import { usePlpContext } from 'plp/context'
import { scrollLastProduct } from 'plp/hooks/useRestorePosition/scrollLastProduct'
import { storeLastProduct } from 'plp/hooks/useRestorePosition/storeLastProduct'
import type { UseRestorePosition } from 'plp/hooks/useRestorePosition/useRestorePosition'
import type { CatalogItem } from 'plp/types/Catalog'
import { ClientProductCard } from 'product-card/components/client/ClientProductCard'
import { ProductDataProvider } from 'product-card/context/ProductDataContext'
import type { Product } from 'product/types'
import { ProductLocations } from 'product/types/ProductLocations'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { applyStylesIf, cx } from 'utils/cx'

import styles from '../Grid.module.scss'

const SAFE_SPACE = 2

export interface CellProps {
	item: CatalogItem
	className: string
	itemIndex?: number
	priority: boolean
	isTwoColumnSize?: boolean
	initialProductData?: Product
	pendingScrollSlot: UseRestorePosition
}

export function Cell(props: CellProps): React.ReactNode {
	const {
		priority,
		item,
		className,
		initialProductData,
		isTwoColumnSize,
		pendingScrollSlot,
	} = props
	const { catalogId } = usePlpContext()

	const [inViewport, setInViewport] = useState(false)
	const containerRef = useRef<HTMLLIElement | null>(null)

	const setInViewportFirstTime = () => {
		if (!inViewport) {
			setInViewport(true)
		}
	}

	useLayoutEffect(() => {
		const viewportMargin = window.innerHeight / SAFE_SPACE
		const CellIntersection = (entries: { isIntersecting: boolean }[]) => {
			const [entry] = entries
			if (entry.isIntersecting) {
				setInViewportFirstTime()
			}
		}
		const observer = new IntersectionObserver(CellIntersection, {
			rootMargin: `${viewportMargin}px 0px ${viewportMargin}px 0px`,
			threshold: 0,
		})
		if (containerRef.current) {
			observer.observe(containerRef.current)
		}
		return () => {
			if (observer && containerRef.current) {
				observer.unobserve(containerRef.current)
				observer.disconnect()
			}
		}
	}, [])

	useEffect(() => {
		const lastProductHandler = () => storeLastProduct(item.id, catalogId)
		containerRef.current?.addEventListener('click', lastProductHandler)
		return () => {
			containerRef.current?.removeEventListener('click', lastProductHandler)
		}
	}, [])

	const clientProductCardChild = (
		<ClientProductCard
			imageSortCriteria={item.images.order}
			productId={item.productId}
			colorId={item.colorId}
			lookId={item.lookId || '00'}
			priority={priority}
			isTwoColumnSize={isTwoColumnSize}
			dataTestId={dataTestIds.plpProductCard}
			location={ProductLocations.Grid}
		/>
	)

	const isEmpty = clientProductCardChild === null

	return (
		<li
			className={cx(className, applyStylesIf(isEmpty, styles.empty))}
			data-from-client='true'
			data-slot={item.id}
			id={`p-${item.id}`}
			ref={(ref) => {
				containerRef.current = ref
				if (ref && item.id === pendingScrollSlot.id) {
					scrollLastProduct(ref, catalogId)
				}
			}}
		>
			{(priority || inViewport) &&
				(initialProductData ? (
					<ProductDataProvider initialProductData={initialProductData}>
						{clientProductCardChild}
					</ProductDataProvider>
				) : (
					clientProductCardChild
				))}
		</li>
	)
}
